<template>

  <shell :title="$t('titles.organizations')">

    <table-tools :actions="tableActions" :headers="headers" v-model:enabled-headers="enabledHeaders" remember-headers="organization-index-new">

      <table-filter>
        <o-text-field
          :label="$t('titles.organization_name')"
          v-model="filters.name"
          clearable
        />
      </table-filter>

      <table-filter>
        <v-select
          :label="$t('titles.status')"
          v-model="filters.live"
          :items="[{title: $t('misc.live'), value: true}, {title: $t('misc.onboarded'), value: false}]"
          clearable
        />
      </table-filter>

    </table-tools>

    <v-data-table
      ref="dataTable"
      :items="organizations"
      :headers="visibleHeaders"
      :sort-by="sortBy"
      show-expand
      v-model:page="page"
      :items-per-page="itemsPerPage"
      :loading="loadingOrgs"
    >

      <template v-slot:item.contactEmail="{ item }">
        <template v-if="item.contactEmail">
          <a :href="'mailto:' + item.contactEmail">{{ item.contactEmail }}</a>
        </template>
      </template>

      <template v-slot:item.live="{ item }">
        <template v-if="item.live">
          {{ $t('misc.live') }}
        </template>
        <template v-if="!item.live">
          {{ $t('misc.onboarded') }}
        </template>
      </template>

      <template v-slot:item.actions="{ item }">

        <ActionGroup
          :actions="[
            {label: $t('actions.edit'), icon: '$edit', event: 'editClicked'},
          ]"
          @edit-clicked="editOrganization(item)"
        />

      </template>

      <template v-slot:expanded-row="{ columns, item }">
        <tr>
          <td :colspan="columns.length">
            {{ $t('titles.number_of_active_locations') }} {{ item.locations.length }}
          </td>
        </tr>
      </template>

      <template v-slot:bottom>
        <DataTablePagination
          :items="organizations"
          v-model:page="page"
          v-model:items-per-page="itemsPerPage"
          @update:page="scrollDataTableIntoView()"
        />
      </template>

    </v-data-table>

  </shell>
  
</template>

<script setup lang="ts">

import { ref, computed, reactive, inject, watch, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTitle } from '@vueuse/core'
import Shell from '../Shell.vue'
import DataTablePagination from '../../DataTablePagination.vue'
import OrganizationModal from './OrganizationModal.vue'
import Organization from '../../../models/Organization'
// @ts-ignore
import { openDialog } from 'vue3-promise-dialog'
import NotificationService from '../../../services/NotificationService'
import { QuerySyncer, BooleanSerializer } from '../../../utils/QuerySyncer'
import type { Can } from '../../../main'

const { t } = useI18n()
const $can = inject('$can') as Can

const title = useTitle()
title.value = t('titles.organizations')

const filters = reactive({
  name: '',
  live: null,
})

new QuerySyncer({
  name: {
    watchable: () => filters.name,
    hydrate: (newValue: any) => filters.name = newValue,
  },
  live: {
    watchable: () => filters.live,
    hydrate: (newValue: any) => filters.live = newValue,
    serializer: BooleanSerializer,
  },
})

const tableActions = computed(() => [
  {
    title: t('actions.reload'),
    icon: '$reload',
    click: loadOrganizations,
    busy: loadingOrgs.value,
  },
  {
    title: t('actions.create_organization'),
    icon: '$create',
    click: createOrganization,
  },
])

/**
 * Pagination
 */

const page = ref(1)
const itemsPerPage = ref(10)

// when anything that affects the main query is changed, reset the page to 1
watch(filters, () => page.value = 1)

/**
 * Organization data
 */

const loadingOrgs = ref<boolean>(false)

function loadOrganizations() {
  
  loadingOrgs.value = true

  Organization.fetchAll()
    .catch((error) => {
      console.error(error)
      NotificationService.error(t('alerts.couldnt_load_organizations'))
    })
    .finally(() => {
      loadingOrgs.value = false
    })

}

/**
 * Data table configs
 */

const sortBy = ref([{ key: 'name' }])

const headers = [
  {title: t('titles.id'), key: 'ID', width: 100, hidden: !$can('seeids')},
  {title: t('titles.organization'), key: 'name', width: 300, hidden: !$can('organizations.manage'), toggleable: false},
  {title: t('titles.contact_name'), key: 'contactName', width: 200},
  {title: t('titles.contact_email'), key: 'contactEmail', width: 300},
  {title: t('titles.contact_phone'), key: 'contactPhone', width: 300},
  {title: t('titles.status'), key: 'live', width: 100},
  {title: '', key: 'actions', sortable: false, hideFromPicker: true},
]

const enabledHeaders = ref([
  'ID', 'name', 'contactName', 'contactEmail', 'contactPhone', 'live', 'actions',
])

const visibleHeaders = computed(() => {
  return headers.filter((header) => {
    return !header.hidden && enabledHeaders.value.includes(header.key)
  })
})

const organizations = computed<Organization[]>(() => {
  
  const query = Organization.query()
    .with('locations', (subquery) => {
      subquery.where('live', true)
    })

  if (filters.live === true || filters.live === false ) {
      query.where('live', filters.live)
    }

  if (filters.name !== null && filters.name.length > 0) {
    query.where((organization: Organization) => {
      return organization.name.toUpperCase().indexOf(
        filters.name.toUpperCase()
      ) !== -1
    })
  }

  return query.get()

})

/**
 * Modal
 */

function createOrganization() {
  openDialog(OrganizationModal, {
    organization: new Organization()
  })
}

function editOrganization(organization: Organization) {
  openDialog(OrganizationModal, {
    organization: organization,
  })
}

/**
 * Miscellaneous
 */

const dataTable = useTemplateRef('dataTable')

function scrollDataTableIntoView() {
  if (dataTable.value) {
    dataTable.value.$el.scrollIntoView()
  }

}

</script>
